import {
  Box,
  Chip,
  FormControl,
  FormHelperText,
  Grid,
  MenuItem,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { NewCampaignContext } from "contexts/NewCampaign";
import moment from "moment";
import { useContext, useEffect } from "react";
import { CustomEvent, Map } from "schemas/functions";

import StyledAutocomplete from "components/Styled/Autocomplete";
import StyledSelect from "components/Styled/Select";
import StyledTextField from "components/Styled/TextField";
import { addBusinessDays } from "utils/time";

import InfoBox from "../../SharedComponents/InfoBox";
import StepperFooter from "../../SharedComponents/StepperFooter";

const EMAILS_PER_DAY_OPTIONS = [5, 10, 20, 25, 30];
const DEFAULT_EMAILS_PER_DAY = 20;
const DEFAULT_TIMEZONE = "America/New_York";

interface Props {
  handleClose: () => void;
  handleUpdateCampaign: (body: Map) => void;
  submitLoading?: boolean;
}
export default function CampaignDetails({
  handleClose,
  handleUpdateCampaign,
  submitLoading = false,
}: Props) {
  const { campaign, setCampaign } = useContext(NewCampaignContext);

  const defaultStartDate = addBusinessDays(1);
  const MIN_DATE = defaultStartDate.toISOString().split("T")[0];
  const DEFAULT_START_DATE = defaultStartDate.toISOString().split("T")[0];

  const handleNext = async () => {
    if (!campaign) return;
    handleUpdateCampaign({
      name: campaign.name,
      startDate: campaign.startDate,
      emailsPerDay: campaign.emailsPerDay ?? DEFAULT_EMAILS_PER_DAY,
      timezone: campaign.timezone,
    });
  };

  useEffect(() => {
    setCampaign((prev) => {
      const updates: Partial<typeof prev> = {};
      if (!prev?.startDate) updates.startDate = DEFAULT_START_DATE;
      if (!prev?.emailsPerDay) updates.emailsPerDay = DEFAULT_EMAILS_PER_DAY;
      if (!prev?.timezone) updates.timezone = DEFAULT_TIMEZONE;
      if (!prev)
        return {
          name: "",
          startDate: DEFAULT_START_DATE,
          emailsPerDay: DEFAULT_EMAILS_PER_DAY,
          timezone: DEFAULT_TIMEZONE,
        };
      return Object.keys(updates).length ? { ...prev, ...updates } : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <InfoBox
        icon="fa-solid fa-bullseye"
        title="What is a Campaign?"
        description="A campaign helps you organize outreach to multiple brands around a specific theme, season, or product category. Perfect for coordinating seasonal pitches or themed content."
      />

      <Box
        sx={{
          mx: { xs: 2.5, md: 4 },
          display: "flex",
          flexDirection: "column",
          gap: 3,
        }}
      >
        {/* Campaign Name */}
        <Box>
          <StyledTextField
            label="Campaign Name"
            required
            fullWidth
            value={campaign?.name || ""}
            onChange={(e: CustomEvent) => {
              setCampaign((prev) => {
                if (!prev) return { name: e.target.value, startDate: null };
                return { ...prev, name: e.target.value };
              });
            }}
            placeholder="e.g. Summer home decor, holiday gift guide"
          />

          <FormHelperText sx={{ color: grey[600], ml: 1 }}>
            Choose a name that describes the theme or focus of this campaign
          </FormHelperText>
        </Box>

        <Grid container gap={2}>
          {/* Start Date */}
          <Box>
            <StyledTextField
              label="Start Date"
              type="date"
              required
              value={campaign?.startDate || DEFAULT_START_DATE}
              onChange={(e: CustomEvent) => {
                const value = e.target.value;
                setCampaign((prev) => {
                  if (!prev) return { name: "", startDate: value };
                  return { ...prev, startDate: value };
                });
              }}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: MIN_DATE }}
              styles={{ minWidth: 300 }}
            />
          </Box>

          {/* Emails Per Day */}
          <Box>
            <FormControl sx={{ minWidth: 300 }}>
              <StyledSelect
                labelId="emails-per-day-label"
                label="Emails you want to send per day *"
                value={campaign?.emailsPerDay ?? DEFAULT_EMAILS_PER_DAY}
                onChange={(e: CustomEvent) => {
                  const value = e.target.value as number;
                  setCampaign((prev) => {
                    if (!prev)
                      return { name: "", startDate: null, emailsPerDay: value };
                    return { ...prev, emailsPerDay: value };
                  });
                }}
                required
              >
                {EMAILS_PER_DAY_OPTIONS.map((n) => (
                  <MenuItem key={n} value={n}>
                    {n} emails / day
                    {n === DEFAULT_EMAILS_PER_DAY ? (
                      <Chip
                        label="Recommended"
                        size="small"
                        sx={{
                          ml: 1,
                          fontSize: 11,
                          fontWeight: 600,
                          height: 20,
                          bgcolor: "purple.light",
                          color: "purple.dark",
                          border: "1px solid",
                          borderColor: "purple.main",
                          "& .MuiChip-label": { px: 1 },
                        }}
                      />
                    ) : null}
                  </MenuItem>
                ))}
              </StyledSelect>
            </FormControl>
          </Box>

          {/* Timezone */}
          <Box>
            <FormControl sx={{ minWidth: 300 }}>
              <StyledAutocomplete
                required
                label="Timezone"
                options={moment.tz.names()}
                value={campaign?.timezone ?? DEFAULT_TIMEZONE}
                onChange={(_: any, newValue: string | null) => {
                  setCampaign((p) => {
                    if (!p) return p;
                    const copy = { ...p };
                    copy.timezone = newValue || undefined;
                    return copy;
                  });
                }}
              />
            </FormControl>
          </Box>
        </Grid>
      </Box>

      <StepperFooter
        handleBack={handleClose}
        handleNext={handleNext}
        nextDisabled={Boolean(!campaign?.name || !campaign?.startDate)}
        nextLoading={submitLoading}
      />
    </>
  );
}
